import { fetchSuggestions } from '../actions/fetchSuggestions';
import { MAX_ATTENDEES_FOR_AVAILABILITY, SERVER_CALL_TIMEOUT } from '../constants';
import createSonoraPerformanceDatapoint from '../dataLogger/createSonoraPerformanceDatapoint';
import { log3sSearchAction } from '../dataLogger/log3sInstrumentation';
import {
    updateAllSuggestions,
    updateNetworkCallOnDispatch,
    updateNetworkCallOnCompletion,
    updateNetworkCallOnTimeout,
} from '../mutators/sonoraMutators';
import fetchUserDataServiceSuggestions from '../utils/fetchUserDataServiceSuggestions';
import getSonoraDataProviders from '../utils/getSonoraDataProviders';
import { getRequiredAttendeesFromRecipients } from '../utils/meetingTimes/getAttendeesFromRecipients';
import type { UserDataServiceSuggestions } from 'owa-editor-sonora-types';
import { GenericKeys } from 'owa-analytics/lib/types/DatapointEnums';
import { trace } from 'owa-trace';
import { DataProviderType } from '@augloop-types/honeybee';
import { orchestrator } from 'satcheljs';

export const fetchSuggestionsOrchestrator = orchestrator(fetchSuggestions, async actionMessage => {
    const { viewState, config, loadTimeDp, updateDp, callInitOps } = actionMessage;

    const userDataServiceDataProviders: DataProviderType[] = getSonoraDataProviders(config);

    // Track the latest network call.
    // Mutators are synchronous so the latestNetworkCallNumber will be updated
    // after updateNetworkCallOnDispatch completes.
    updateNetworkCallOnDispatch(viewState);
    const networkCallNumber: number = viewState.latestNetworkCallNumber;

    // Log the processing time for fetching suggestions.
    const performanceDatapoint = createSonoraPerformanceDatapoint(loadTimeDp, {
        isEmptyQuery: viewState.queryString.length === 0,
        dataSource: userDataServiceDataProviders.map(type => DataProviderType[type]).toString(),
        isAL: false,
    });

    const userDataServiceSuggestions = await Promise.race([
        fetchUserDataServiceSuggestions(
            viewState.queryString,
            userDataServiceDataProviders,
            config,
            callInitOps,
            JSON.stringify(
                getRequiredAttendeesFromRecipients(
                    viewState.recipients,
                    MAX_ATTENDEES_FOR_AVAILABILITY,
                    true /* shouldKeepInternalAttendeesOnly */
                )
            )
        ),
        createTimeoutPromise(SERVER_CALL_TIMEOUT),
    ])
        .then(val => {
            // We will enter here if the call went through.
            updateNetworkCallOnCompletion(viewState);
            return val;
        })
        .catch(error => {
            // We will enter here if we timeout.
            updateNetworkCallOnTimeout(viewState);
            return {};
        });

    performanceDatapoint.end();

    // If we made another network call in the meanwhile, discard this one.
    if (networkCallNumber !== viewState.latestNetworkCallNumber) {
        trace.warn('[Sonora] Rejecting response since a subsequent call has been made.');
        return;
    }

    // send instructmentation to 3s (if applicable).
    log3sSearchAction(viewState.results, 'searchdone');

    updateAllSuggestions(
        viewState,
        userDataServiceSuggestions,
        <number>performanceDatapoint.getData(GenericKeys.e2eTimeElapsed),
        updateDp
    );
});

/**
 * Creates a promise that rejects after running for timeoutNum milliseconds.
 */
function createTimeoutPromise(timeoutNum: number): Promise<UserDataServiceSuggestions> {
    return new Promise((resolve, reject) => {
        setTimeout(function () {
            const timeoutError: Error = new Error('timeout');
            reject(timeoutError);
        }, timeoutNum);
    });
}
